import React, { useState } from "react";
import Highcharts from "highcharts/highstock";
import HighchartsReact from "highcharts-react-official";
import {
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  ToggleButton,
  ToggleButtonGroup,
  Box,
  Typography,
  Tabs,
  Tab,
} from "@mui/material";
import VuiTypography from "components/VuiTypography";
import { formatPriceDisplay } from "utilities/utilites";

const MarketChart = ({ motherTokenData = {}, childTokenData = {} }) => {
  const [chartType, setChartType] = useState("column");
  const [selectedMetric, setSelectedMetric] = useState("priceUsd");

  Highcharts.setOptions({
    lang: {
    // Pre-v9 legacy settings
        rangeSelectorFrom: 'From',
        rangeSelectorTo:"→",
        rangeSelectorTo: 'To'
    }
});
  // Extract historical data based on metric
// Extract values for mother and child tokens
const formatChartData = (tokenData) => {
  if (!tokenData || Object.keys(tokenData).length === 0) return 0;
  
  return selectedMetric === "liquidity"
    ? tokenData.liquidity?.usd || 0
    : selectedMetric === "fdv"
    ? tokenData.fdv || 0
    : selectedMetric === "holders" 
    ? tokenData.holdersCount || 0
    : parseFloat(tokenData.priceUsd) || 0;
};

// Get the values
const motherValue = formatChartData(motherTokenData);
const childValue = formatChartData(childTokenData);

const chartOptions = {
  chart: {
    type: chartType,
    backgroundColor: "transparent",
    height: 400,
  },
  title: {
    text: `Market Analysis - ${selectedMetric.toUpperCase()}`,
    style: { color: "#fff", fontSize: "16px", fontWeight: "bold" },
  },
  xAxis: {
    type: 'category',
    categories: ['Mother', 'Child'],
    labels: { style: { color: "#fff", fontSize: "12px" } },
    lineColor: "#fff",
    tickColor: "#fff",
  },
  yAxis: {
    title: { text: selectedMetric.toUpperCase(), style: { color: "#fff" } },
    labels: { 
      style: { color: "#fff", fontSize: "12px" },
      formatter: function () {
        if(selectedMetric === "priceUsd"){
          return this.value.toFixed(8);
        }
        return this.value;
      } 
    },
    gridLineColor: "rgba(255, 255, 255, 0.1)",
    allowDecimals: true
  },
  legend: {
    enabled: true,
    layout: "horizontal",
    align: "center",
    verticalAlign: "top",
    itemStyle: { color: "#fff", fontSize: "12px" },
  },
  tooltip: {
    backgroundColor: "#1E293B",
    borderColor: "#fff",
    style: { color: "#fff" },
    formatter: function () {
      if(selectedMetric === "priceUsd"){
        return `<b>${this.series.name}</b>: ${this.y.toFixed(8)}`;
      }
      return `<b>${this.series.name}</b>: ${this.y}`;
    }
  },
  plotOptions: {
    column: {
      borderRadius: 5,
      borderWidth: 0,
      pointPadding: 0.2,
      dataLabels: {
        enabled: true,
        style: { color: "#fff", fontSize: "12px", fontWeight: "bold" },
        formatter: function () {
          if(selectedMetric === "priceUsd"){
            return this.y.toFixed(8);
          }
          return this.y;
        }
      },
    },
    series: {
      animation: {
        duration: 500
      }
    }
  },
  rangeSelector: {
   enabled:false
  },
  navigator: {
    enabled: false // Disable for bar/column charts comparing just two values
  },
  scrollbar: {
    enabled: false // Disable for bar/column charts comparing just two values
  },
  series: [
    {
      name: `${motherTokenData?.baseToken?.name || "Mother"}`,
      data: [[0, motherValue]], // For stockChart, points need [x, y] format
      color: "#00bfff",
    },
    {
      name: `${childTokenData?.baseToken?.name || "Child"}`,
      data: [[1, childValue]], // Use index 1 for second category
      color: "#ff4500",
    }
  ]
};
  

  return (
    <Box sx={{ borderRadius: "12px", p: 3 }}>
      {/* Controls Container */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr auto", // Tabs take remaining space, select stays fixed
          alignItems: "center", // Keep elements aligned
          gap: 2, // Adds spacing
          mb: 3,
        }}
      >
        {/* Chart Type Selection */}
        <Tabs
          value={chartType}
          onChange={(event, newType) => newType && setChartType(newType)}
          sx={{
            background: "transparent",
          }}
        >
             <Tab value="column" label="Column" sx={{ color: "#fff", fontWeight: "bold" }} />
          <Tab value="bar" label="Bar" sx={{ color: "#fff", fontWeight: "bold" }} />
         
        </Tabs>

        {/* Metric Selection */}
        <FormControl
          sx={{
            width: "200px", // Fixed width for consistency
            borderRadius: "8px",
            backgroundColor: "#1E293B", // Dark background
            p: 1,
          }}
        >
          <VuiTypography
            variant="button"
            color="white"
            sx={{ fontSize: "14px", fontWeight: "bold", mb: 1 }}
          >
            Chart Metric
          </VuiTypography>
          <Select
            value={selectedMetric}
            onChange={(e) => setSelectedMetric(e.target.value)}
            displayEmpty
            sx={{
              color: "#fff",
              width: "100%", // Ensures full width
              "& .MuiOutlinedInput-notchedOutline": { border: "none" }, // Removes border
              "&.Mui-focused": { backgroundColor: "#334155" }, // Slight highlight on focus
              borderRadius: "6px",
              padding: "10px",
              justifyContent: "center",
            }}
          >
            <MenuItem value="priceUsd">Price (USD)</MenuItem>
            <MenuItem value="liquidity">Liquidity (USD)</MenuItem>
            <MenuItem value="fdv">FDV</MenuItem>
            <MenuItem value="holders">Holders</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Chart Display */}
      {formatChartData(motherTokenData).length === 0 &&
      formatChartData(childTokenData).length === 0 ? (
        <Typography
          sx={{
            color: "#94A3B8",
            textAlign: "center",
            fontSize: "14px",
            fontWeight: "bold",
          }}
        >
          No Data Available
        </Typography>
      ) : (
        <HighchartsReact
          highcharts={Highcharts}
          constructorType={"stockChart"}
          options={chartOptions}
        />
      )}
    </Box>
  );
};

export default MarketChart;
